import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { useKitZip } from '../assets/useKitZip';
import { useWatermarkChoice } from '../../../watermarkUpsell/choice/useWatermarkChoice';
import { kitMessages } from './kitMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Kit.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface KitZipSlotProps {
  accountId: number;
  catalogItemUuid: string;
  /** The ready keeper files of the song (the files of the zip). */
  fileCount: number;
  /** The files of the zip that carry the Music24 watermark (design: trial-upsell a2). */
  watermarkedCount?: number;
  songTitle?: string;
}

/**
 * The kit zip slot (design assets-rethink b1): keepers only (decision 9), "Build the zip", then
 * "Download the zip" when the link is ready (a larger kit gets the link by email too, `useKitZip`).
 * On Free and the trial, "Build the zip" of watermarked files opens the choice first (trial-upsell a2).
 */
const KitZipSlotComponent: React.FC<KitZipSlotProps> = (props) => {
  const { accountId, catalogItemUuid, fileCount, watermarkedCount = 0, songTitle } = props;
  const intl = useIntl();
  const zip = useKitZip({ accountId, catalogItemUuid });
  const { gate } = useWatermarkChoice();
  const { download } = zip;
  const build = useCallback(() => {
    gate(watermarkedCount > 0, {
      surface: 'zip',
      label: null,
      songTitle: songTitle ?? null,
      thumbUrl: null,
      shape: 'square',
      width: null,
      height: null,
      preview: false,
      fileCount: watermarkedCount,
      onContinue: download,
    });
  }, [download, gate, songTitle, watermarkedCount]);
  return (
    <div className={clsx(styles.slot, styles.slotEmpty)} data-testid="kit-slot-zip">
      <div className={styles.thumb}>
        <span className={styles.ghost}>zip</span>
      </div>
      <div className={styles.body}>
        <div className={styles.name}>
          <b>{intl.formatMessage(kitMessages.slotZip)}</b>
          <span className={styles.st}>{intl.formatMessage(kitMessages.zipFiles, { count: fileCount })}</span>
        </div>
        <span className={styles.line}>{intl.formatMessage(kitMessages.zipLine)}</span>
      </div>
      <div className={styles.foot}>
        {zip.readyUrl !== null ? (
          <a className={styles.link} href={zip.readyUrl} download>
            {intl.formatMessage(kitMessages.downloadZip)}
          </a>
        ) : (
          <button type="button" className={styles.link} onClick={build} disabled={zip.isZipping || fileCount === 0}>
            {intl.formatMessage(kitMessages.buildZip)}
          </button>
        )}
      </div>
    </div>
  );
};

export const KitZipSlot = TypedMemo(KitZipSlotComponent);
